import { useCallback, useEffect, useRef, useState } from "react";
import type { AudioVideoFacade } from "amazon-chime-sdk-js";
import { videoEventBus, type ReactionEvent } from "@core/features/video";
import { usePlatformStore } from "@platform/store";

const REACTION_TOPIC = "emoji-reaction";
const MESSAGE_LIFETIME_MS = 1_500;
const THROTTLE_MS = 1_000;
const SELF_REACTION_TTL_MS = 4_000;

/**
 * Hook for sending emoji reactions. Broadcasts to other attendees via the
 * Chime data-message channel and dispatches a local reaction so the sender
 * sees their own reaction on their own tile immediately.
 *
 * Exposes `isThrottled` so the UI can render a brief disabled state during
 * the cooldown window instead of silently rejecting fast repeat clicks.
 */
export function useReactionBroadcast(
  audioVideo: AudioVideoFacade | null,
  localAttendeeId: string | null
): {
  sendReaction: (emoji: string) => boolean;
  isThrottled: boolean;
} {
  const lastSentAtRef = useRef<number>(0);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Self-reaction cleanup timers. The receive path (ChimeObserverState)
  // schedules removeReaction for remote reactions, but Chime doesn't echo
  // to the sender, so the sender's own tile has no receive-side cleanup —
  // we must schedule it here.
  const selfCleanupTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set()
  );
  const [isThrottled, setIsThrottled] = useState(false);

  // Clear pending self-cleanup timers when the hook unmounts (e.g. user
  // leaves the meeting). The removeReaction fallback is idempotent, so a
  // stale timer firing after unmount is harmless — this is just hygiene.
  useEffect(() => {
    const timers = selfCleanupTimersRef.current;
    return () => {
      for (const t of timers) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const sendReaction = useCallback(
    (emoji: string): boolean => {
      if (!audioVideo || !localAttendeeId) return false;

      const now = Date.now();
      if (now - lastSentAtRef.current < THROTTLE_MS) return false;
      lastSentAtRef.current = now;

      const reaction: ReactionEvent = {
        id: `${localAttendeeId}-${now}-${Math.random().toString(36).slice(2, 8)}`,
        emoji,
        attendeeId: localAttendeeId,
        timestamp: now,
      };

      // local dispatch — realtimeSendDataMessage does not echo to sender,
      // so this is the only path that renders our own reaction. We must
      // also schedule the TTL cleanup ourselves for the same reason.
      usePlatformStore.getState().addReaction(localAttendeeId, reaction);
      const cleanupTimer = setTimeout(() => {
        usePlatformStore
          .getState()
          .removeReaction(localAttendeeId, reaction.id);
        selfCleanupTimersRef.current.delete(cleanupTimer);
      }, SELF_REACTION_TTL_MS);
      selfCleanupTimersRef.current.add(cleanupTimer);

      try {
        audioVideo.realtimeSendDataMessage(
          REACTION_TOPIC,
          JSON.stringify(reaction),
          MESSAGE_LIFETIME_MS
        );
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.debug("[useReactionBroadcast] data message send failed:", e);
        }
      }

      videoEventBus.emitEvent("videochat:reaction:sent", { emoji });

      // Flip throttled flag so UI can disable the button briefly.
      setIsThrottled(true);
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = setTimeout(() => {
        setIsThrottled(false);
        throttleTimerRef.current = null;
      }, THROTTLE_MS);

      return true;
    },
    [audioVideo, localAttendeeId]
  );

  return { sendReaction, isThrottled };
}
